import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// ---------------------------------------------------------------------------
// Ungelesene Nachrichten für die Navigations-Badges (Kopfzeile + Bottom-Nav).
// Vorher existierte der Zähler nur im Dashboard: Wer nicht zufällig dort
// vorbeischaute, erfuhr schlicht nicht, dass eine Antwort auf seine Anfrage
// da ist — für einen Marktplatz, der von schnellen Antworten lebt, die
// teuerste Lücke der Navigation. Leichtgewichtiger Zähl-Endpoint, alle 60 s
// und bei Rückkehr in den Tab aktualisiert.
// ---------------------------------------------------------------------------
const UnreadContext = createContext({ unread: 0, neuLaden: () => {} });
export const useUnread = () => useContext(UnreadContext);

export function UnreadProvider({ children }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);

  // Von außen aufrufbar (01.10.2026): Wer ein Gespräch öffnet, setzt seinen
  // Zähler auf dem Server zurück — der Punkt in der Kopfzeile stand trotzdem
  // bis zum nächsten Minutenabruf da. Messages.jsx lädt ihn jetzt sofort nach.
  const neuLaden = useCallback(() => {
    if (!user) return;
    api.get("/conversations/unread-count")
      .then(({ data }) => setUnread(data?.count || 0))
      .catch(() => {}); // Badge ist nie kritisch — Fehler still schlucken
  }, [user]);

  useEffect(() => {
    if (!user) { setUnread(0); return undefined; }
    let aktiv = true;
    const laden = () => {
      api.get("/conversations/unread-count")
        .then(({ data }) => { if (aktiv) setUnread(data?.count || 0); })
        .catch(() => {}); // Badge ist nie kritisch — Fehler still schlucken
    };
    laden();
    const intervall = setInterval(laden, 60000);
    const beiRueckkehr = () => { if (document.visibilityState === "visible") laden(); };
    document.addEventListener("visibilitychange", beiRueckkehr);
    return () => {
      aktiv = false;
      clearInterval(intervall);
      document.removeEventListener("visibilitychange", beiRueckkehr);
    };
  }, [user]);

  return <UnreadContext.Provider value={{ unread, neuLaden }}>{children}</UnreadContext.Provider>;
}
